/**
 * `/dashboard` — the pinned dashboard, one or more boards shown as Chrome-like tabs.
 *
 * Each board is a draggable, resizable grid of pinned views (`PinBoard`), each re-rendered live
 * against the current snapshot via `SpecView` — never a screenshot of what the chart looked like
 * when it was pinned. `PinBoard` is keyed by the active dashboard id so switching tabs always
 * mounts a fresh grid instance: no layout, drag state, or in-flight save can leak from one board
 * onto another. Empty state points at `/chat`, where every view the agent presents can be pinned
 * here.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCreateDashboard,
  useDashboardPins,
  useDashboards,
  useDeleteDashboard,
  useDeletePin,
  useUpdateDashboard,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { useLocalStorage } from "@/lib/use-local-storage";

import { DashboardTabs } from "./dashboard-tabs";
import { PinBoard } from "./pin-board";

const ACTIVE_DASHBOARD_KEY = "dashboard.activeId";

export function DashboardPage() {
  const dashboardsQuery = useDashboards();
  const createDashboardMutation = useCreateDashboard();
  const updateDashboardMutation = useUpdateDashboard();
  const deleteDashboardMutation = useDeleteDashboard();
  const deletePinMutation = useDeletePin();
  const [storedActiveId, setStoredActiveId] = useLocalStorage<string | null>(
    ACTIVE_DASHBOARD_KEY,
    null,
  );
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dashboards = dashboardsQuery.data ?? [];
  // The remembered tab wins when it still exists; otherwise fall back to the first board —
  // `listDashboards` never returns empty, so there's always somewhere to land.
  const activeId =
    (storedActiveId && dashboards.some((d) => d.id === storedActiveId)
      ? storedActiveId
      : dashboards[0]?.id) ?? null;

  const pinsQuery = useDashboardPins(activeId);

  function handleCreate() {
    createDashboardMutation.mutate(
      {},
      {
        onSuccess: (created) => {
          if (created) {
            setStoredActiveId(created.id);
          }
        },
        onError: (err) => {
          logger.warn("[dashboard] failed to create board", err);
        },
      },
    );
  }

  function handleRename(id: string, name: string) {
    updateDashboardMutation.mutate(
      { dashboardId: id, name },
      {
        onError: (err) => {
          logger.warn("[dashboard] failed to rename board", err);
        },
      },
    );
  }

  function requestDelete(id: string) {
    setDeleteError(null);
    setDeleteTargetId(id);
  }

  function confirmDelete() {
    if (!deleteTargetId) {
      return;
    }
    const id = deleteTargetId;
    deleteDashboardMutation.mutate(
      { dashboardId: id },
      {
        onSuccess: () => {
          setDeleteTargetId(null);
          setDeleteError(null);
          // The deleted board can no longer be the remembered tab — clear it so the fallback
          // (the first remaining board) takes over on the next render.
          if (activeId === id) {
            setStoredActiveId(null);
          }
        },
        onError: (err) => {
          const status = (err as { status?: number } | undefined)?.status;
          logger.warn("[dashboard] failed to delete board", err);
          setDeleteError(
            status === 409
              ? "Every dashboard needs at least one board — this is the last one, so it can't be deleted."
              : "Couldn't delete this board — try again.",
          );
        },
      },
    );
  }

  const deleteTarget = dashboards.find((d) => d.id === deleteTargetId) ?? null;

  return (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <main className="min-h-0 flex-1 overflow-y-auto px-6 py-8">
        <div className="mb-4 space-y-1">
          <p className="text-primary text-sm font-semibold tracking-[0.2em] uppercase">
            Pinned dashboard
          </p>
          <p className="text-muted max-w-2xl text-sm leading-6">
            Every view below re-resolves against the current snapshot every time this page loads —
            never a screenshot from the moment you pinned it. Drag a card to move it, drag its
            corner to resize.
          </p>
        </div>

        {dashboardsQuery.isLoading || !activeId ? (
          <div className="flex justify-center py-16">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : dashboardsQuery.error ? (
          <p className="text-muted-foreground text-sm">Couldn&apos;t load your dashboards.</p>
        ) : (
          <>
            <DashboardTabs
              dashboards={dashboards}
              activeId={activeId}
              onSelect={setStoredActiveId}
              onCreate={handleCreate}
              creating={createDashboardMutation.isPending}
              onRename={handleRename}
              onDeleteRequest={requestDelete}
            />
            <div className="border-border bg-card min-h-[420px] rounded-tr-2xl rounded-b-2xl border p-4">
              {pinsQuery.isLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="text-muted h-6 w-6 animate-spin" />
                </div>
              ) : pinsQuery.error ? (
                <p className="text-muted-foreground text-sm">
                  Couldn&apos;t load this board&apos;s pinned views.
                </p>
              ) : pinsQuery.data?.length ? (
                <PinBoard
                  key={activeId}
                  dashboardId={activeId}
                  pins={pinsQuery.data}
                  onUnpin={(pinId, conversationId) =>
                    deletePinMutation.mutate({ pinId, conversationId })
                  }
                  unpinningId={
                    deletePinMutation.isPending
                      ? (deletePinMutation.variables?.pinId ?? null)
                      : null
                  }
                />
              ) : (
                <div className="p-10 text-center">
                  <p className="text-foreground font-semibold">No pinned views yet</p>
                  <p className="text-muted mx-auto mt-2 max-w-md text-sm leading-6">
                    Ask the agent a question about your SKU portfolio at <b>/chat</b> — every chart
                    it shows as evidence can be pinned here, where it stays live against the current
                    snapshot.
                  </p>
                  <Link to="/chat" className="mt-4 inline-block">
                    <Button>Open the agent</Button>
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <Dialog open={!!deleteTargetId} onOpenChange={(open) => !open && setDeleteTargetId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</DialogTitle>
            <DialogDescription>
              This also deletes every view pinned to this board. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <p role="alert" className="text-destructive text-sm">
              {deleteError}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTargetId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteDashboardMutation.isPending}
            >
              {deleteDashboardMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete board"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
